import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { BatchSubmitClaimsProgressResponse } from '@store/generated/claims';

const activePulse = keyframes`
  0%, 100% { opacity: 0.48; transform: scaleY(0.78); }
  50% { opacity: 1; transform: scaleY(1); }
`;

const terminalStatuses = new Set(['completed', 'completed_with_failures', 'failed']);
const problemStatuses = new Set(['blocked', 'failed', 'outcome_unknown']);

const headline = (progress: BatchSubmitClaimsProgressResponse) => {
  const status = progress.status ?? 'queued';
  const submitted = progress.submitted ?? 0;
  const attention =
    (progress.blocked ?? 0) + (progress.failed ?? 0) + (progress.outcome_unknown ?? 0);
  if (status === 'completed') return `${submitted} claim${submitted === 1 ? '' : 's'} sent`;
  if (status === 'completed_with_failures') {
    return `${submitted} sent · ${attention} need${attention === 1 ? 's' : ''} attention`;
  }
  if (status === 'failed') return 'Claim submission stopped';
  if (status === 'queued') return `Preparing ${progress.total} claims`;
  return `Sending claims · ${progress.processed ?? 0} of ${progress.total}`;
};

interface ClaimBatchProgressTrayProps {
  progress: BatchSubmitClaimsProgressResponse;
  onDismiss: () => void;
  onReviewClaim?: (claimId: string) => void;
}

export const ClaimBatchProgressTray: React.FC<ClaimBatchProgressTrayProps> = ({
  progress,
  onDismiss,
  onReviewClaim,
}) => {
  const status = progress.status ?? 'queued';
  const isTerminal = terminalStatuses.has(status);
  const items = progress.items ?? [];
  const attentionItems = items.filter(item => problemStatuses.has(item.status ?? ''));
  const firstAttentionItem = attentionItems[0];
  const processed = Math.min(progress.total, Math.max(0, progress.processed ?? 0));
  const progressPercent = progress.total > 0 ? (processed / progress.total) * 100 : 0;

  return (
    <Box
      aria-live="polite"
      data-testid="claim-batch-progress-tray"
      sx={{
        bgcolor: 'background.paper',
        borderColor: 'divider',
        borderTop: '1px solid',
        bottom: 0,
        boxShadow: '0 -10px 32px rgba(15, 23, 42, 0.12)',
        left: 0,
        px: { xs: 2, md: 3 },
        py: 1.5,
        position: 'fixed',
        right: 0,
        zIndex: 1200,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.25, md: 2.5 }}
        sx={{ alignItems: { xs: 'stretch', md: 'center' }, mx: 'auto', maxWidth: 1180 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 230 }}>
          {isTerminal ? (
            attentionItems.length > 0 ? (
              <ErrorOutlineIcon color="error" fontSize="small" />
            ) : (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            )
          ) : (
            <Box
              sx={{
                animation: `${activePulse} 1.1s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                bgcolor: 'primary.main',
                borderRadius: 999,
                height: 16,
                width: 5,
              }}
            />
          )}
          <Box>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 750 }}>
              {headline(progress)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {isTerminal
                ? attentionItems.length > 0
                  ? 'Sent claims are complete; review only the exceptions.'
                  : 'Every selected claim reached the clearinghouse.'
                : 'You can keep working while these drain in the background.'}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <LinearProgress
            aria-label="Claim submission progress"
            aria-valuetext={`${processed} of ${progress.total} claims processed`}
            sx={{
              bgcolor: 'action.disabledBackground',
              borderRadius: 999,
              height: 8,
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                transition: 'transform 240ms ease',
              },
              '@media (prefers-reduced-motion: reduce)': {
                '& .MuiLinearProgress-bar': { transition: 'none' },
              },
            }}
            value={progressPercent}
            variant="determinate"
          />
        </Box>

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          {firstAttentionItem && onReviewClaim && (
            <Button
              onClick={() => onReviewClaim(firstAttentionItem.claim_id)}
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: 'none' }}
              variant="contained"
            >
              Review {attentionItems.length === 1 ? 'claim' : `${attentionItems.length} claims`}
            </Button>
          )}
          {isTerminal && (
            <Button onClick={onDismiss} size="small" sx={{ textTransform: 'none' }}>
              Dismiss
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
